import { useEffect } from 'react';

/**
 * Hook to fix aria-hidden accessibility issues
 * This prevents the warning about aria-hidden on elements with focused descendants
 */
export function useAriaHiddenFix() {
  useEffect(() => {
    // Function to remove aria-hidden from root and body
    const removeAriaHidden = () => {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.removeAttribute('aria-hidden');
      }
      document.body.removeAttribute('aria-hidden');
    };

    // Remove aria-hidden on mount
    removeAriaHidden();

    // Set up a mutation observer to watch for aria-hidden being added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-hidden'
        ) {
          const target = mutation.target as HTMLElement;
          if (target.id === 'root' || target === document.body) {
            // Check if there are any focused elements in the document
            const focusedElement = document.activeElement;
            if (focusedElement && focusedElement !== document.body) {
              // If there's a focused element, remove aria-hidden
              removeAriaHidden();
            }
          }
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);
}
